import React from 'react';
import { Card, Typography, Space, Tag, Divider, QRCode } from 'antd';
import { 
  ShopOutlined, 
  UserOutlined, 
  PhoneOutlined, 
  EnvironmentOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  FireOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface ThermalLabelProps {
  data: {
    order: {
      id: number;
      status: string;
      totalAmount: number;
      createdAt: string;
      deliveryDate: string;
    };
    customer: {
      companyName: string;
      contactName: string;
      contactPhone: string;
      address: string;
    };
    supplier: {
      companyName: string;
      contactPhone: string;
    };
    items: Array<{
      id: number;
      name: string;
      quantity: number;
      price: number;
      weight: number;
      calories?: number | null;
      isVegan: boolean;
      isLenten: boolean;
      isHalal: boolean;
    }>;
    combos?: Array<{
      id: number;
      comboId: number;
      title: string;
      quantity: number;
      unitPrice: number;
      priceTotal: number;
      selectionsSnapshot: any;
    }>;
    qrCode: string;
    totalWeight: number;
    totalCalories: number;
  };
}

const ThermalLabel: React.FC<ThermalLabelProps> = ({ data }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'confirmed': return 'blue';
      case 'in_preparation': return 'purple';
      case 'in_delivery': return 'cyan';
      case 'delivered': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Ожидает';
      case 'confirmed': return 'Подтвержден';
      case 'in_preparation': return 'Готовится';
      case 'in_delivery': return 'В доставке';
      case 'delivered': return 'Доставлен';
      case 'cancelled': return 'Отменен';
      default: return status;
    }
  };

  return (
    <div style={{ 
      width: '80mm', 
      minHeight: '120mm', 
      padding: '8px', 
      border: '2px solid #000',
      backgroundColor: '#fff',
      fontFamily: 'monospace',
      fontSize: '12px',
      lineHeight: '1.2'
    }}>
      {/* Заголовок */}
      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <Title level={4} style={{ margin: 0, fontSize: '16px' }}>
          🍽️ ЗАКАЗ #{data.order.id}
        </Title>
        <Tag color={getStatusColor(data.order.status)}>
          {getStatusText(data.order.status)}
        </Tag>
      </div>

      {/* QR-код */}
      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <QRCode 
          value={data.qrCode} 
          size={120}
          style={{ border: '1px solid #000' }}
        />
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Информация о заказчике */}
      <div style={{ marginBottom: '8px' }}>
        <Text strong style={{ fontSize: '14px' }}>
          <ShopOutlined /> {data.customer.companyName}
        </Text>
        <br />
        <Text>
          <UserOutlined /> {data.customer.contactName}
        </Text>
        <br />
        <Text>
          <PhoneOutlined /> {data.customer.contactPhone}
        </Text>
        <br />
        <Text>
          <EnvironmentOutlined /> {data.customer.address}
        </Text>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Информация о поставщике */}
      <div style={{ marginBottom: '8px' }}>
        <Text strong style={{ fontSize: '14px' }}>
          🏪 {data.supplier.companyName}
        </Text>
        <br />
        <Text>
          <PhoneOutlined /> {data.supplier.contactPhone}
        </Text>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Содержимое заказа */}
      <div style={{ marginBottom: '8px' }}>
        <Text strong style={{ fontSize: '14px' }}>
          📋 СОДЕРЖИМОЕ ЗАКАЗА:
        </Text>
        <br />
        {data.items.map((item, index) => (
          <div key={item.id} style={{ marginBottom: '4px' }}>
            <Text>
              {index + 1}. {item.name} x{item.quantity}
            </Text>
            <br />
            <Space size={4}>
              {item.isVegan && <Tag color="green">Веган</Tag>}
              {item.isLenten && <Tag color="blue">Постное</Tag>}
              {item.isHalal && <Tag color="orange">Халяль</Tag>}
            </Space>
            <br />
            <Text type="secondary" style={{ fontSize: '10px' }}>
              {item.weight}г, {typeof item.calories === 'number' ? `${item.calories}ккал` : '—'}
            </Text>
          </div>
        ))}

        {(data.combos || []).length > 0 && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <Text strong style={{ fontSize: '14px' }}>
              🧩 КОМБО:
            </Text>
            <br />
            {(data.combos || []).map((c, idx) => {
              const groups = Array.isArray(c.selectionsSnapshot) ? c.selectionsSnapshot : [];
              return (
                <div key={c.id} style={{ marginBottom: '4px' }}>
                  <Text>
                    {idx + 1}. {c.title} x{c.quantity}
                  </Text>
                  {groups.length > 0 ? (
                    <div style={{ marginTop: 2 }}>
                      {groups.map((g: any) => (
                        <div key={g.groupId || g.title}>
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            {g.title}: {(g.items || []).map((it: any) => it.name).join(', ') || '—'}
                          </Text>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </>
        )}
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Общая информация */}
      <div style={{ marginBottom: '8px' }}>
        <Text strong style={{ fontSize: '14px' }}>
          📊 ОБЩАЯ ИНФОРМАЦИЯ:
        </Text>
        <br />
        <Text>
          <AppstoreOutlined /> Вес: {data.totalWeight}г
        </Text>
        <br />
        <Text>
          <FireOutlined /> Калории: {data.totalCalories}ккал
        </Text>
        <br />
        <Text>
          💰 Сумма: {data.order.totalAmount}₽
        </Text>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Временные метки */}
      <div style={{ textAlign: 'center' }}>
        <Text style={{ fontSize: '10px' }}>
          <CalendarOutlined /> Создан: {formatDate(data.order.createdAt)}
        </Text>
        <br />
        <Text style={{ fontSize: '10px' }}>
          Меню (день выбора): {formatDate(data.order.deliveryDate)}
        </Text>
      </div>
    </div>
  );
};

export default ThermalLabel;
